import { ok } from "@hightouch/lib/util/result";
import { useFlags } from "launchdarkly-react-client-sdk";
import type {
  AdDrilldownState,
  DrillScope,
  InsightsLoad,
  ListedAd,
} from "src/pages/ad-studio/insights/performance/types";
import {
  blendLoad,
  cubeLoad,
  useInsightsQuery,
} from "src/pages/ad-studio/insights/performance/use-insights-query";
import { adDrilldownStateOf } from "src/pages/ad-studio/insights/performance/utils/drill";
import { memberLabel } from "src/pages/ad-studio/insights/performance/utils/meta";
import {
  adDailyQuery,
  adHeaderQuery,
  adTagsBlend,
  adTagsSubject,
} from "src/pages/ad-studio/insights/performance/utils/query";
import { mapQueryResult } from "src/pages/ad-studio/insights/performance/utils/query-result";
import {
  dailyColumns,
  tagCategoriesOf,
} from "src/pages/ad-studio/insights/performance/utils/rows";

const adTagsLoad = ({
  drill,
  id,
}: {
  drill: DrillScope;
  id: string;
}): InsightsLoad<readonly string[]> => {
  const { ad } = drill.meta.views;
  const [first, ...rest] = tagCategoriesOf(ad);
  return first === undefined
    ? {
        subject: adTagsSubject(id),
        key: ["ad-tags", "untagged-view"],
        load: () => Promise.resolve(ok([])),
      }
    : blendLoad({
        blend: adTagsBlend({ drill, id, categories: [first, ...rest] }),
        wire: ad.wire,
      });
};

export function useAdDrilldown({
  drill,
  adId,
  listed,
}: {
  drill: DrillScope;
  adId: string;
  listed: ListedAd | "no-list-row";
}): { state: AdDrilldownState } {
  const { adStudioInsightsSuggestedActions } = useFlags();
  const view = drill.meta.views.ad;
  const { wire } = view;
  const detail = useInsightsQuery(
    cubeLoad({ cube: adHeaderQuery({ drill, id: adId }), wire }),
  );
  const tags = useInsightsQuery(adTagsLoad({ drill, id: adId }));
  const points = useInsightsQuery(
    cubeLoad({ cube: adDailyQuery({ drill, id: adId }), wire }),
  );
  const { ctr, frequency } = dailyColumns(view);
  const labels = {
    ctr: memberLabel(view, ctr.member),
    frequency: memberLabel(view, frequency.member),
  };
  return {
    state: adDrilldownStateOf({
      drill,
      adId,
      listed,
      detail,
      tags,
      daily: mapQueryResult(points, (value) => ({ points: value, labels })),
      suggestedActions:
        adStudioInsightsSuggestedActions === true ? "flag-on" : "flag-off",
    }),
  };
}
